import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '@/components/layout/Layout';
import DocumentTitle from '@/components/layout/DocumentTitle';
import SubmittingOverlay from '@/components/ui/SubmittingOverlay';
import LoginPage from '@/pages/auth/LoginPage';
import RecoverPasswordPage from '@/pages/auth/RecoverPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import Loading from '@/components/ui/Loading';
import { useAppearance } from '@/contexts/AppearanceContext';

// Lazy load pages for better performance
const DashboardPage = React.lazy(() => import('@/pages/DashboardPage'));
const ContactsPage = React.lazy(() => import('@/pages/ContactsPage'));
const AddressesPage = React.lazy(() => import('@/pages/AddressesPage'));
const InteractionsPage = React.lazy(() => import('@/pages/InteractionsPage'));
const SchedulePage = React.lazy(() => import('@/pages/SchedulePage'));
const TeamsPage = React.lazy(() => import('@/pages/TeamsPage'));
const StudiesPage = React.lazy(() => import('@/pages/StudiesPage'));
const RegionsPage = React.lazy(() => import('@/pages/RegionsPage'));
const UsersPage = React.lazy(() => import('@/pages/UsersPage'));
const ReportsPage = React.lazy(() => import('@/pages/ReportsPage'));
const NotificationsPage = React.lazy(() => import('@/pages/NotificationsPage'));
const ProfilePage = React.lazy(() => import('@/pages/ProfilePage'));
const SettingsPage = React.lazy(() => import('@/pages/SettingsPage'));
const SysInfoPage = React.lazy(() => import('@/pages/SysInfoPage'));

// Admin pages
const PermissionsPage = React.lazy(() => import('@/pages/admin/PermissionsPage'));
const PermissionsMatrixPage = React.lazy(() => import('@/pages/admin/PermissionsMatrixPage'));
const PermissionsManualPage = React.lazy(() => import('@/pages/admin/PermissionsManualPage'));

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Loading fullScreen text="Carregando..." />;
  }

  if (!isAuthenticated) {
    const from = encodeURIComponent(location.pathname + location.search + location.hash);
    return <Navigate to={`/login?from=${from}`} replace />;
  }

  // When there is a token but the user is not yet loaded, wait to validate session
  if (isAuthenticated && !user) {
    return <Loading fullScreen text="Validando sessão..." />;
  }

  return <>{children}</>;
};

// Public Route component (redirects to dashboard if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// App Routes component
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/recuperar-senha"
        element={
          <PublicRoute>
            <RecoverPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/redefinir-senha"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <React.Suspense fallback={<Loading text="Carregando dashboard..." />}>
              <DashboardPage />
            </React.Suspense>
          }
        />
        <Route
          path="contacts"
          element={
            <React.Suspense fallback={<Loading text="Carregando contatos..." />}>
              <ContactsPage />
            </React.Suspense>
          }
        />
        <Route
          path="addresses"
          element={
            <React.Suspense fallback={<Loading text="Carregando endereços..." />}>
              <AddressesPage />
            </React.Suspense>
          }
        />
        <Route
          path="interactions"
          element={
            <React.Suspense fallback={<Loading text="Carregando interações..." />}>
              <InteractionsPage />
            </React.Suspense>
          }
        />
        <Route
          path="agenda"
          element={
            <React.Suspense fallback={<Loading text="Carregando agenda..." />}>
              <SchedulePage />
            </React.Suspense>
          }
        />
        <Route
          path="teams"
          element={
            <React.Suspense fallback={<Loading text="Carregando equipes..." />}>
              <TeamsPage />
            </React.Suspense>
          }
        />
        <Route
          path="regions"
          element={
            <React.Suspense fallback={<Loading text="Carregando regiões..." />}>
              <RegionsPage />
            </React.Suspense>
          }
        />
        <Route
          path="studies"
          element={
            <React.Suspense fallback={<Loading text="Carregando estudos..." />}>
              <StudiesPage />
            </React.Suspense>
          }
        />
        <Route
          path="users"
          element={
            <React.Suspense fallback={<Loading text="Carregando usuários..." />}>
              <UsersPage />
            </React.Suspense>
          }
        />
        <Route
          path="reports"
          element={
            <React.Suspense fallback={<Loading text="Carregando relatórios..." />}>
              <ReportsPage />
            </React.Suspense>
          }
        />
        <Route
          path="notifications"
          element={
            <React.Suspense fallback={<Loading text="Carregando notificações..." />}>
              <NotificationsPage />
            </React.Suspense>
          }
        />
        <Route
          path="profile"
          element={
            <React.Suspense fallback={<Loading text="Carregando perfil..." />}>
              <ProfilePage />
            </React.Suspense>
          }
        />
        <Route
          path="settings"
          element={
            <React.Suspense fallback={<Loading text="Carregando configurações..." />}>
              <SettingsPage />
            </React.Suspense>
          }
        />
        <Route
          path="admin/permissions"
          element={
            <React.Suspense fallback={<Loading text="Carregando gerenciamento de permissões..." />}>
              <PermissionsPage />
            </React.Suspense>
          }
        />
        <Route
          path="admin/permissions/matrix"
          element={
            <React.Suspense fallback={<Loading text="Carregando matriz de permissões..." />}>
              <PermissionsMatrixPage />
            </React.Suspense>
          }
        />
        <Route
          path="admin/permissions/manual"
          element={
            <React.Suspense fallback={<Loading text="Carregando manual de permissões..." />}>
              <PermissionsManualPage />
            </React.Suspense>
          }
        />
        <Route
          path="sysinfo"
          element={
            <React.Suspense fallback={<Loading text="Carregando informações do sistema..." />}>
              <SysInfoPage />
            </React.Suspense>
          }
        />
      </Route>

      {/* Catch all route - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App component
const App: React.FC = () => {
  const { theme } = useAppearance();
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <DocumentTitle />
      <SubmittingOverlay />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={theme === 'dark' ? 'dark' : 'light'}
      />
      <AppRoutes />
    </div>
  );
};

export default App;
